import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { PageTransition } from "@/components/PageTransition";
import { SearchBar } from "@/components/SearchBar";
import { TermCard } from "@/components/TermCard";
import { TERMS_PAGE_SIZES, useTermsPage } from "@/hooks/useTerms";
import { normalizeForCompare } from "@/lib/normalizeText";
import { Search as SearchIcon, ArrowLeft, Plus, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import siteLogo from "@/assets/brandLogo";
import { useLanguage } from "@/contexts/LanguageContext";

const Search = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const [page, setPage] = useState(1);
  const pageSize = TERMS_PAGE_SIZES.search;

  useEffect(() => {
    setPage(1);
  }, [query]);

  const {
    data: pageResult,
    isLoading,
    error,
    refetch,
    progress,
  } = useTermsPage({
    page,
    page_size: pageSize,
    search: query,
    enabled: query.trim().length > 0,
  });

  // Results refine as the catalog streams in; keep a hint until it's complete.
  const searchingMore = query.trim().length > 0 && !progress.complete;

  const terms = pageResult?.terms;
  const totalCount = pageResult?.pagination?.count ?? terms?.length ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const safePage = Math.min(page, totalPages);

  return (
    <PageTransition>
      <div className="min-h-screen">
        {/* Hero Header */}
        <section className="relative z-10 overflow-visible bg-gradient-to-br from-[hsl(220,40%,13%)] via-[hsl(220,38%,18%)] to-[hsl(218,35%,22%)]">
          {/* Animated Background */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="bg-primary/20 float absolute -right-20 -top-20 h-60 w-60 rounded-full blur-3xl" />
            <div className="bg-gold/15 float-delayed absolute -bottom-20 -left-20 h-80 w-80 rounded-full blur-3xl" />
          </div>

          {/* Decorative Pattern */}
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 50'%3E%3Ccircle cx='10' cy='10' r='1' fill='%23fff'/%3E%3Ccircle cx='30' cy='15' r='1' fill='%23fff'/%3E%3Ccircle cx='50' cy='8' r='1' fill='%23fff'/%3E%3Ccircle cx='70' cy='20' r='1' fill='%23fff'/%3E%3Ccircle cx='90' cy='12' r='1' fill='%23fff'/%3E%3C/svg%3E")`,
              backgroundSize: "150px 75px",
            }}
          />

          <div className="container relative py-5 md:py-12">
            <div className="flex flex-col items-center text-center">
              <div className="animate-fade-in-up relative mb-6">
                <div className="bg-primary/20 absolute inset-0 scale-150 rounded-full blur-xl" />
                <img
                  src={siteLogo}
                  alt={t("footer.orgLogoAlt")}
                  className="relative h-16 w-auto max-w-[min(320px,90vw)] object-contain md:h-20"
                />
              </div>
              <h1
                className="animate-fade-in-up mb-3 font-serif text-3xl font-bold text-white md:text-5xl"
                style={{ animationDelay: "0.1s" }}
              >
                {t("search.title")}
              </h1>
              {query && (
                <p
                  className="animate-fade-in-up mb-8 text-base text-white/70 md:text-lg"
                  style={{ animationDelay: "0.2s" }}
                >
                  {t("search.resultsFor", { query })}
                </p>
              )}
              <div
                className="animate-fade-in-up relative z-30 w-full max-w-lg"
                style={{ animationDelay: "0.3s" }}
              >
                <SearchBar variant="hero" />
              </div>
            </div>
          </div>

          {/* Bottom Wave */}
          <div className="absolute bottom-0 left-0 right-0">
            <svg
              viewBox="0 0 1440 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="w-full"
            >
              <path
                d="M0 40V20C360 35 720 5 1080 20C1260 27.5 1350 32.5 1440 35V40H0Z"
                fill="white"
              />
            </svg>
          </div>
        </section>

        {/* Results */}
        <section className="py-5 md:py-12">
          <div className="container">
            <Button
              variant="ghost"
              onClick={() => navigate("/browse")}
              className="text-muted-foreground hover:text-foreground md:mb-8 mb-4"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t("search.backToBrowse")}
            </Button>

            {isLoading ? (
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="from-secondary/50 to-secondary/30 h-48 animate-pulse rounded-xl bg-gradient-to-br"
                  />
                ))}
              </div>
            ) : error ? (
              <div className="bg-destructive/5 border-destructive/20 rounded-2xl border py-16 text-center">
                <div className="bg-destructive/10 mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full">
                  <SearchIcon className="text-destructive h-10 w-10" />
                </div>
                <h3 className="text-foreground mb-3 font-serif text-xl font-semibold">
                  {t("search.loadErrorTitle")}
                </h3>
                <p className="text-muted-foreground mx-auto mb-8 max-w-md">
                  {t("search.loadErrorBody")}
                </p>
                <Button
                  onClick={() => refetch()}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  {t("common.retry")}
                </Button>
              </div>
            ) : terms && terms.length > 0 ? (
              <>
                <div className="bg-primary/10 border-primary/20 md:mb-8 mb-4 flex flex-wrap items-center gap-3 rounded-xl border p-4">
                  <div className="bg-primary/20 flex h-10 w-10 items-center justify-center rounded-full">
                    <SearchIcon className="text-primary h-5 w-5" />
                  </div>
                  <p className="text-foreground min-w-0 flex-1 text-sm sm:text-base">
                    {totalCount === 1
                      ? t("search.foundSingular", { count: totalCount })
                      : t("search.foundPlural", { count: totalCount })}
                  </p>
                  {searchingMore && (
                    <span className="text-muted-foreground flex items-center gap-2 text-xs">
                      <span className="border-primary/30 border-t-primary h-3.5 w-3.5 animate-spin rounded-full border-2" />
                      {t("browse.loadingMore")}
                    </span>
                  )}
                </div>
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {terms.map((term, i) => {
                    const isExact =
                      !!query.trim() &&
                      normalizeForCompare(term.word) ===
                        normalizeForCompare(query);
                    return (
                      <TermCard
                        key={term.id}
                        id={term.id}
                        word={term.word}
                        meaningEnglish={term.meaning_english}
                        meaningHindi={term.meaning_hindi}
                        meaningGujarati={term.meaning_gujarati}
                        pronunciation={term.pronunciation}
                        index={i}
                        highlight={query}
                        isExactMatch={isExact}
                      />
                    );
                  })}
                </div>
                {totalPages > 1 ? (
                  <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={safePage <= 1}
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      {t("browse.paginationPrev")}
                    </Button>
                    <span className="text-muted-foreground px-2 text-sm tabular-nums">
                      {t("browse.paginationPageOf", {
                        current: safePage,
                        total: totalPages,
                      })}
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={safePage >= totalPages}
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    >
                      {t("browse.paginationNext")}
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                ) : null}
              </>
            ) : searchingMore ? (
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="from-secondary/50 to-secondary/30 h-48 animate-pulse rounded-xl bg-gradient-to-br"
                  />
                ))}
              </div>
            ) : (
              <div className="from-secondary/30 to-secondary/10 border-border/50 rounded-2xl border bg-gradient-to-br py-16 text-center">
                <div className="bg-muted mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full">
                  <SearchIcon className="text-muted-foreground h-10 w-10" />
                </div>
                <h3 className="text-foreground mb-3 font-serif text-xl font-semibold">
                  {t("search.noResults")}
                </h3>
                <p className="text-muted-foreground mx-auto mb-8 max-w-md">
                  {t("search.noResultsHint")}
                </p>
                <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
                  <Button onClick={() => navigate("/browse")} variant="outline">
                    {t("search.browseAll")}
                  </Button>
                  <Button
                    onClick={() => navigate("/contribute")}
                    className="bg-primary hover:bg-primary/90"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {t("common.addTerm")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </PageTransition>
  );
};

export default Search;
